import { theme, spacing, typography, isMobile } from '../styles/theme';
import SimpleHeader from '../components/SimpleHeader';

export default function PrivacyPolicy() {
  return (
    <>
        <SimpleHeader />
        <div style={{
            backgroundColor: theme.bg.primary,
            position: 'fixed',
            top: '72px',
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: 'auto',
            padding: spacing['3xl'],
            ...(isMobile() && {
                top: '70px',
                padding: spacing.lg,
            })
        }}>
            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                backgroundColor: 'white',
                padding: spacing['3xl'],
                borderRadius: '12px',
                boxShadow: theme.shadow.medium,
                // Mobile adjustments
                ...(window.innerWidth <= 768 && {
                padding: spacing.lg,
                })
            }}>
                <div style={{ marginBottom: spacing.lg }}>
                    <a 
                        href="/" 
                        style={{
                            color: theme.brand.primary,
                            textDecoration: 'none',
                            fontSize: typography.fontSize.base,
                            fontWeight: typography.fontWeight.medium,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: spacing.xs,
                        }}
                    >
                        ← Back to Judgy
                    </a>
                </div>
                <h1 style={{
                    color: theme.text.primary,
                    fontSize: typography.fontSize['3xl'],
                    fontWeight: typography.fontWeight.bold,
                    marginBottom: spacing.xl,
                    fontFamily: typography.fontFamily.primary,
                }}>
                    Privacy Policy
                </h1>

                <p style={{ color: theme.text.secondary, marginBottom: spacing.lg }}>
                    <strong>Last Updated:</strong> October 27, 2025
                </p>
                <p style={{color: theme.text.secondary, marginBottom: spacing.lg}}>
                    <strong>Version:</strong> 1.0
                </p>

                <div style={{
                    color: theme.text.primary,
                    fontSize: typography.fontSize.base,
                    lineHeight: typography.lineHeight.relaxed,
                    fontFamily: typography.fontFamily.primary,
                }}>
                    {/* Introduction */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                                fontSize: typography.fontSize.xl,
                                fontWeight: typography.fontWeight.bold,
                                marginBottom: spacing.md,
                                color: theme.brand.primary,
                            }}
                        >
                            1. Introduction
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            Welcome to Judgy ("we," "our," or "us"). We are committed to protecting your personal information 
                            and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                            your information when you use our website and services at askjudgy.com.
                        </p>
                        <p style={{ marginBottom: spacing.md }}>
                            Judgy is operated from Luxembourg and complies with the General Data Protection Regulation (GDPR) 
                            and other applicable privacy laws.
                        </p>
                    </section>

                    {/* Information We Collect */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            2. Information We Collect
                        </h2>
                
                        <h3 style={{
                            fontSize: typography.fontSize.lg,
                            fontWeight: typography.fontWeight.semibold,
                            marginBottom: spacing.sm,
                        }}>
                            2.1 Information You Provide
                        </h3>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Account Information:</strong> When you create an account, we collect your email address 
                                and display name (if provided).
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Chat Messages:</strong> Questions you submit to Judgy are sent to our AI service 
                                (OpenAI) for processing but are <strong>not permanently stored</strong> on our servers. 
                                Messages exist only during your browser session and are not retained after you close the page. 
                                We may temporarily log messages for debugging purposes (retained for 7 days maximum).
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Feedback:</strong> When you flag answers or provide feedback, we collect your comments 
                                and the associated question/answer pair.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Payment Information:</strong> Payment details are processed by Stripe. We do not store 
                                your full credit card information on our servers.
                            </li>
                        </ul>

                        <h3 style={{
                            fontSize: typography.fontSize.lg,
                            fontWeight: typography.fontWeight.semibold,
                            marginBottom: spacing.sm,
                        }}>
                            2.2 Information Collected Automatically
                        </h3>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Usage Data:</strong> We collect information about how you interact with our service, 
                                including pages visited, features used, and time spent on the site.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Device Information:</strong> Browser type, operating system, device type, and IP address.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Cookies:</strong> We use cookies and similar tracking technologies. See Section 5 for details.
                            </li>
                        </ul>
                    </section>

                    {/* How We Use Your Information */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            3. How We Use Your Information
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>We use your information to:</p>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>Provide and maintain our service</li>
                            <li style={{ marginBottom: spacing.sm }}>Process your chat queries using AI (powered by OpenAI)</li>
                            <li style={{ marginBottom: spacing.sm }}>Manage your account and subscription status</li>
                            <li style={{ marginBottom: spacing.sm }}>Process payments through Stripe</li>
                            <li style={{ marginBottom: spacing.sm }}>Improve our AI responses based on feedback</li>
                            <li style={{ marginBottom: spacing.sm }}>Analyze usage patterns to enhance user experience</li>
                            <li style={{ marginBottom: spacing.sm }}>Send important updates about our service</li>
                            <li style={{ marginBottom: spacing.sm }}>Detect and prevent fraud or abuse</li>
                        </ul>
                    </section>

                    {/* Third-Party Services */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            4. Third-Party Services
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            We use the following third-party services that may collect your information:
                        </p>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Firebase (Google):</strong> Authentication and user management. 
                                <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" 
                                style={{ color: theme.brand.primary, marginLeft: '4px' }}>Privacy Policy</a>
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>OpenAI:</strong> AI processing of your questions. Your messages are sent to OpenAI's API. 
                                <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" 
                                style={{ color: theme.brand.primary, marginLeft: '4px' }}>Privacy Policy</a>
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Stripe:</strong> Payment processing. 
                                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" 
                                style={{ color: theme.brand.primary, marginLeft: '4px' }}>Privacy Policy</a>
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Google Analytics:</strong> Usage analytics and site performance. 
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" 
                                style={{ color: theme.brand.primary, marginLeft: '4px' }}>Privacy Policy</a>
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Scryfall:</strong> Magic: The Gathering card data and images. 
                                <a href="https://scryfall.com/docs/privacy" target="_blank" rel="noopener noreferrer" 
                                style={{ color: theme.brand.primary, marginLeft: '4px' }}>Privacy Policy</a>
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Sentry:</strong> Error tracking and monitoring. 
                                <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" 
                                style={{ color: theme.brand.primary, marginLeft: '4px' }}>Privacy Policy</a>
                            </li>
                        </ul>
                    </section>

                    {/* Cookies */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            5. Cookies and Tracking Technologies
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            We use cookies and similar tracking technologies to improve your experience:
                        </p>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Essential Cookies:</strong> Required for authentication and basic site functionality.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Analytics Cookies:</strong> Google Analytics tracks usage patterns to help us improve.
                            </li>
                        </ul>
                        <p style={{ marginBottom: spacing.md }}>
                            You can control cookies through our cookie consent banner or your browser settings. 
                            Note that disabling certain cookies may limit site functionality.
                        </p>
                    </section>

                    {/* Data Retention */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            6. Data Retention
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            We retain your information for as long as necessary to provide our service and comply with legal obligations:
                        </p>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Account Data:</strong> Retained while your account is active and for 30 days after deletion.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Chat Messages:</strong> Not permanently stored. Messages are processed in real-time 
                                and not retained on our servers. Temporary debug logs are deleted after 7 days.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Feedback:</strong> Anonymized and retained for quality improvement purposes.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Payment Records:</strong> Retained for 7 years for tax and accounting purposes.
                            </li>
                        </ul>
                        <p style={{ marginBottom: spacing.md }}>
                            <strong>Note:</strong> While we don't store your chat messages, they are sent to OpenAI 
                            for processing. OpenAI may retain data according to their privacy policy (typically 30 days 
                            for API usage). See <a href="https://openai.com/policies/privacy-policy" target="_blank" 
                            rel="noopener noreferrer" style={{ color: theme.brand.primary }}>OpenAI's Privacy Policy</a> 
                            for details.
                        </p>
                    </section>

                    {/* Your Rights (GDPR) */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            7. Your Rights (GDPR)
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            If you are in the European Economic Area (EEA) or United Kingdom, you have the following rights:
                        </p>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Right to Access:</strong> Request a copy of your personal data.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Right to Rectification:</strong> Request correction of inaccurate data.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Right to Erasure:</strong> Request deletion of your account and data.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Right to Restriction:</strong> Request limitation of processing your data.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Right to Data Portability:</strong> Receive your data in a portable format.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Right to Object:</strong> Object to processing based on legitimate interests.
                            </li>
                            <li style={{ marginBottom: spacing.sm }}>
                                <strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing at any time.
                            </li>
                        </ul>
                        <p style={{ marginBottom: spacing.md }}>
                            To exercise these rights, contact us at <a href="mailto:support@askjudgy.com" 
                            style={{ color: theme.brand.primary }}>support@askjudgy.com</a> or delete your account through 
                            the settings page.
                        </p>
                    </section>

                    {/* California Rights (CCPA) */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            8. California Privacy Rights (CCPA)
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            If you are a California resident, you have the right to:
                        </p>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>Know what personal information we collect and how we use it</li>
                            <li style={{ marginBottom: spacing.sm }}>Request deletion of your personal information</li>
                            <li style={{ marginBottom: spacing.sm }}>Opt-out of the sale of personal information</li>
                        </ul>
                        <p style={{ marginBottom: spacing.md }}>
                            <strong>Note:</strong> We do not sell your personal information to third parties.
                        </p>
                    </section>

                    {/* Data Security */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            9. Data Security
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            We implement appropriate technical and organizational measures to protect your data:
                        </p>
                        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl }}>
                            <li style={{ marginBottom: spacing.sm }}>Encrypted connections (HTTPS)</li>
                            <li style={{ marginBottom: spacing.sm }}>Secure authentication via Firebase</li>
                            <li style={{ marginBottom: spacing.sm }}>Regular security audits and monitoring</li>
                            <li style={{ marginBottom: spacing.sm }}>Access controls and data minimization</li>
                        </ul>
                        <p style={{ marginBottom: spacing.md }}>
                            However, no method of transmission over the internet is 100% secure. While we strive to 
                            protect your information, we cannot guarantee absolute security.
                        </p>
                    </section>

                    {/* Children's Privacy */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            10. Children's Privacy
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            Our service is not intended for children under 16 years of age. We do not knowingly collect 
                            personal information from children under 16. If you believe we have collected information from 
                            a child under 16, please contact us immediately.
                        </p>
                    </section>

                    {/* Changes to Policy */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            11. Changes to This Privacy Policy
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            We may update this Privacy Policy from time to time. We will notify you of significant changes 
                            by posting the new policy on this page and updating the "Last Updated" date. Your continued use 
                            of the service after changes constitutes acceptance of the updated policy.
                        </p>
                    </section>

                    {/* Contact */}
                    <section style={{ marginBottom: spacing['2xl'] }}>
                        <h2 style={{
                            fontSize: typography.fontSize.xl,
                            fontWeight: typography.fontWeight.bold,
                            marginBottom: spacing.md,
                            color: theme.brand.primary,
                        }}>
                            12. Contact Us
                        </h2>
                        <p style={{ marginBottom: spacing.md }}>
                            If you have questions about this Privacy Policy or our data practices, please contact us:
                        </p>
                        <p style={{ marginBottom: spacing.md }}>
                            <strong>Email:</strong> <a href="mailto:support@askjudgy.com" 
                            style={{ color: theme.brand.primary }}>support@askjudgy.com</a><br />
                            <strong>Website:</strong> <a href="https://askjudgy.com" 
                            style={{ color: theme.brand.primary }}>askjudgy.com</a><br />
                            <strong>Location:</strong> Luxembourg
                        </p>
                    </section>

                    {/* Back to Home */}
                    <div style={{
                        textAlign: 'center',
                        marginTop: spacing['3xl'],
                        marginBottom: spacing['3xl'],
                    }}>
                        <a 
                            href="/" 
                            style={{
                                color: theme.brand.primary,
                                textDecoration: 'none',
                                fontSize: typography.fontSize.lg,
                                fontWeight: typography.fontWeight.semibold,
                            }}
                        >
                            ← Back to Judgy
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
}